/* RC controller uses Local's drawer, search and navigation custom elements. */
(() => {
  'use strict';
  if (window.RCHeader) return;
  const instances = new WeakMap();
  const summary = group => group.querySelector(':scope > summary');
  const setOpen = (group, open) => {
    group.open = open;
    summary(group).setAttribute('aria-expanded', String(open));
    if (!open) group.querySelectorAll('details[open]').forEach(child => setOpen(child, false));
  };
  const focusables = root => [...root.querySelectorAll('a[href], button, input:not([type="hidden"]), select, summary, [tabindex="0"]')].filter(el => {
    if (el.disabled || el.closest('[hidden]')) return false;
    if (getComputedStyle(el).visibility === 'hidden') return false;
    for (let parent = el.parentElement; parent && parent !== root; parent = parent.parentElement) {
      if (getComputedStyle(parent).display === 'none') return false;
      if (parent.matches('details:not([open])') && summary(parent) !== el) return false;
    }
    return true;
  });

  class Header {
    constructor(header) {
      this.header = header;
      this.abort = new AbortController();
      this.listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, {...options, signal: this.abort.signal});
      this.drawer = document.getElementById('site-menu-sidebar');
      this.roots = [header];
      if (header.hasAttribute('data-sticky-header')) this.createSticky();
      this.roots.push(this.drawer);
      this.roots.forEach(root => this.bindNavigation(root));
      const cart = document.getElementById('site-cart-sidebar');
      [...this.roots, cart].filter(Boolean).forEach(root => {
        this.listen(root, 'click', event => this.click(event), {capture: true});
        this.listen(root, 'keydown', event => this.keydown(event), {capture: true});
      });
      const overlay = document.querySelector('.site-overlay');
      if (overlay) this.listen(overlay, 'click', event => this.click(event));
      // Outside clicks only dismiss our own navigation/search, never consume events.
      this.listen(document, 'click', event => {
        this.roots.forEach(root => {
          if (!root.contains(event.target)) { this.closeNavigation(root); this.search(root, false); }
        });
      });
      this.listen(document, 'focusin', event => this.focusin(event));
      this.listen(window, 'resize', () => {
        if (this.resizeFrame != null) return;
        this.resizeFrame = requestAnimationFrame(() => {
          this.resizeFrame = null;
          this.resize();
        });
      });
      this.lastScroll = window.scrollY;
      this.listen(window, 'scroll', () => this.scroll(), {passive: true});
      document.querySelectorAll('#main > div:not(.inert-inside)').forEach(el => el.setAttribute('data-js-inert', ''));
      window.inertElems = document.querySelectorAll('[data-js-inert]');
      this.resize();
      this.drawerObserver = new MutationObserver(() => {
        // opened changes immediately; the animation class follows 15 ms later.
        if (this.drawer.opened && !this.drawerWasOpen) this.resetMobile();
        if (!this.drawer.opened && this.drawerWasOpen && this.activeDrawer === this.drawer) this.restoreDrawer();
        this.drawerWasOpen = this.drawer.opened;
      });
      this.drawerObserver.observe(this.drawer, {attributes: true, attributeFilter: ['class']});
      this.cartObserver = new MutationObserver(records => {
        for (const record of records) {
          const count = record.target.nodeType === Node.TEXT_NODE ? record.target.parentElement : record.target;
          const label = count.closest('[data-rc-cart-label]');
          if (label) label.setAttribute('aria-label', label.dataset.rcCartLabel.replace('__count__', count.textContent));
        }
      });
      this.roots.forEach(root => root.querySelectorAll('[data-header-cart-count]').forEach(count => {
        this.cartObserver.observe(count, {childList: true, characterData: true, subtree: true});
      }));
    }

    createSticky() {
      const sticky = document.createElement('div');
      sticky.className = 'sticky-header site-header rc-header';
      sticky.setAttribute('data-js-inert', '');
      sticky.innerHTML = this.header.querySelector('.rc-header__container').outerHTML;
      const ids = new Map();
      sticky.querySelectorAll('[id]').forEach(el => {
        ids.set(el.id, `${el.id}-sticky`);
        el.id = ids.get(el.id);
      });
      for (const attr of ['aria-controls', 'aria-labelledby', 'for', 'data-js-focus-overlay']) {
        sticky.querySelectorAll(`[${attr}]`).forEach(el => {
          el.setAttribute(attr, el.getAttribute(attr).split(' ').map(id => ids.get(id) || id).join(' '));
        });
      }
      document.body.append(sticky);
      this.sticky = sticky;
      this.roots.push(sticky);
    }

    bindNavigation(root) {
      root.querySelectorAll('[data-rc-group]').forEach(group => {
        this.listen(group, 'toggle', () => summary(group).setAttribute('aria-expanded', String(group.open)));
        this.listen(summary(group), 'click', event => {
          event.preventDefault();
          this.toggleGroup(group);
        });
        if (group.closest('.rc-header-nav--rc_desktop') && group.classList.contains('rc-header-nav__group')) {
          this.listen(group, 'pointerenter', event => {
            if (event.pointerType !== 'touch') {
              if (this.pinnedGroup !== group) this.pinnedGroup = null;
              this.openGroup(group, true);
            }
          });
          this.listen(group, 'pointerleave', () => {
            if (this.pinnedGroup !== group && !group.contains(document.activeElement)) setOpen(group, false);
          });
          this.listen(group, 'focusout', event => {
            if (!group.contains(event.relatedTarget)) { setOpen(group, false); this.pinnedGroup = null; }
          });
        }
      });
    }

    openGroup(group, open) {
      if (open && group.closest('.rc-header-nav--rc_desktop')) {
        group.parentElement.parentElement.querySelectorAll(':scope > li > details[open]').forEach(other => {
          if (other !== group) setOpen(other, false);
        });
      }
      setOpen(group, open);
    }

    toggleGroup(group) {
      if (group.matches('.rc-header-nav--rc_desktop > ul > li > details')) {
        const open = !(group.open && this.pinnedGroup === group);
        this.openGroup(group, open);
        this.pinnedGroup = open ? group : null;
      } else this.openGroup(group, !group.open);
    }

    resetMobile() {
      this.drawer.querySelectorAll('[data-rc-group]').forEach(group => {
        setOpen(group, Boolean(this.drawer.dataset.rcMobileOpenItem) && group.dataset.rcHandle === this.drawer.dataset.rcMobileOpenItem);
      });
    }

    closeNavigation(root) {
      root.querySelectorAll('.rc-header-nav--rc_desktop details[open]').forEach(group => setOpen(group, false));
      if (this.pinnedGroup && root.contains(this.pinnedGroup)) this.pinnedGroup = null;
    }

    search(root, open, restore = false) {
      const panel = root.querySelector('[data-rc-search-panel]');
      if (!panel) return;
      panel.hidden = !open;
      root.querySelector('[data-rc-search-toggle]').setAttribute('aria-expanded', String(open));
      if (open) {
        this.closeNavigation(root);
        panel.querySelector('input').focus();
      } else {
        panel.querySelector('.search-results-overlay').classList.remove('active');
        panel.querySelector('search-form').clearSearchResults?.();
        if (restore) root.querySelector('[data-rc-search-toggle]').focus();
      }
    }

    click(event) {
      const target = event.target;
      const opener = target.closest('[data-js-sidebar-handle][aria-controls]');
      if (opener) {
        const drawer = document.getElementById(opener.getAttribute('aria-controls'));
        if (!drawer?.show) return;
        event.preventDefault();
        if (this.activeDrawer && this.activeDrawer !== drawer) this.activeDrawer.hide();
        this.roots.forEach(root => { this.closeNavigation(root); this.search(root, false); });
        this.activeDrawer = drawer;
        this.drawerOpener = opener;
        opener.setAttribute('aria-expanded', 'true');
        if (drawer === this.drawer) this.resetMobile();
        drawer.show();
        drawer.querySelector('[data-js-close]')?.focus();
        return;
      }
      const close = target.closest('sidebar-drawer [data-js-close]');
      if (close || target.closest('.site-overlay')) {
        const drawer = close?.closest('sidebar-drawer') || this.activeDrawer;
        if (drawer !== this.drawer && drawer !== this.activeDrawer) return;
        if (drawer?.opened) {
          event.preventDefault();
          event.stopPropagation();
          drawer.hide();
          this.restoreDrawer();
        }
        return;
      }
      const toggle = target.closest('[data-rc-search-toggle]');
      if (toggle) {
        const root = toggle.closest('.rc-header');
        this.search(root, toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      if (target.closest('a[href]') && this.drawer.contains(target) && this.drawer.opened) {
        this.drawer.hide();
        this.restoreDrawer();
      }
      this.roots.forEach(root => {
        if (!target.closest('[data-rc-navigation]') || target.closest('[data-rc-navigation] a')) this.closeNavigation(root);
        if (!root.contains(target) || target.classList.contains('search-results-overlay')) this.search(root, false);
      });
    }

    restoreDrawer() {
      if (!this.activeDrawer) return;
      this.activeDrawer.setAttribute('aria-hidden', 'true');
      document.querySelectorAll(`[data-js-sidebar-handle][aria-controls="${this.activeDrawer.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'false'));
      this.drawerOpener?.focus();
      this.activeDrawer = null;
      this.drawerOpener = null;
    }

    keydown(event) {
      const target = event.target;
      const group = target.closest('[data-rc-group]');
      const searchRoot = target.closest('.rc-header');
      const drawer = this.activeDrawer?.opened && this.activeDrawer.contains(target) ? this.activeDrawer :
        this.drawer.opened && this.drawer.contains(target) ? this.drawer : null;
      if (event.key === 'Escape') {
        this.pinnedGroup = null;
        // Only consume Escape from an open surface owned by this controller.
        const openedGroup = target.closest('[data-rc-group][open]');
        if (openedGroup) { setOpen(openedGroup, false); summary(openedGroup).focus(); }
        else if (searchRoot?.querySelector('[data-rc-search-toggle][aria-expanded="true"]')) this.search(searchRoot, false, true);
        else if (drawer) { drawer.hide(); this.restoreDrawer(); }
        else {
          const hoveredGroup = this.roots.map(root => root.querySelector('.rc-header-nav--rc_desktop details[open]')).find(Boolean);
          if (!hoveredGroup) return;
          setOpen(hoveredGroup, false); summary(hoveredGroup).focus();
        }
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.key === 'Tab' && drawer) {
        const items = focusables(drawer);
        const first = items[0], last = items.at(-1);
        if (items.length && ((event.shiftKey && (target === first || !drawer.contains(target))) || (!event.shiftKey && (target === last || !drawer.contains(target))))) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        }
      }
      if (target.matches('[data-js-sidebar-handle]') && event.key === ' ') {
        event.preventDefault(); target.click(); return;
      }
      if (group && target === summary(group) && ['Enter', ' '].includes(event.key)) {
        event.preventDefault(); this.toggleGroup(group); return;
      }
      if (!group || !group.closest('.rc-header-nav--rc_desktop') || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.openGroup(group, true);
      const items = focusables(group).filter(el => el !== summary(group));
      const index = items.indexOf(target);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : event.key === 'ArrowDown' ? (index + 1) % items.length : (index <= 0 ? items.length : index) - 1;
      items[next]?.focus();
    }

    focusin(event) {
      this.roots.forEach(root => {
        const panel = root.querySelector('[data-rc-search-panel]');
        if (panel && !panel.hidden && !panel.contains(event.target) && event.target !== root.querySelector('[data-rc-search-toggle]')) this.search(root, false);
      });
      const input = event.target.closest('.rc-header [data-js-search-input]');
      if (!input) return;
      const features = document.getElementById('shopify-features');
      if (!features || !JSON.parse(features.textContent).predictiveSearch) return;
      const overlay = document.getElementById(input.dataset.jsFocusOverlay);
      overlay.style.display = '';
      overlay.classList.add('active');
      if (!document.body.classList.contains('predictive-script-loaded') && !customElements.get('search-form')) {
        document.body.classList.add('predictive-script-loaded');
        const script = document.createElement('script');
        script.src = window.KROWN.settings.predictive_search_script;
        document.head.append(script);
      }
    }

    resize() {
      const height = document.getElementById('root-height');
      if (height) height.textContent = `:root { --window-height: ${window.innerHeight}px; }`;
      this.roots.forEach(root => {
        this.closeNavigation(root);
        root.querySelector('scrollable-navigation')?.checkNav?.();
      });
    }

    scroll() {
      if (!this.sticky) return;
      const current = window.scrollY;
      if (Math.abs(current - this.lastScroll) <= 5 && current > 0) return;
      const show = current > 0 && current < this.lastScroll && this.header.getBoundingClientRect().bottom < -100;
      this.sticky.classList.toggle('show', show);
      if (!show) { this.closeNavigation(this.sticky); this.search(this.sticky, false); }
      this.lastScroll = current;
    }

    destroy() {
      this.abort.abort();
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
      this.drawerObserver.disconnect();
      this.cartObserver.disconnect();
      if (this.activeDrawer?.opened) { this.activeDrawer.hide(); this.restoreDrawer(); }
      this.sticky?.remove();
      window.inertElems = document.querySelectorAll('[data-js-inert]');
    }
  }

  window.RCHeader = {
    mount(header) { if (!instances.has(header)) instances.set(header, new Header(header)); },
    unmount(header) { instances.get(header)?.destroy(); instances.delete(header); }
  };
  window.dispatchEvent(new Event('rc:header-ready'));
})();
